import React from "react";
import { Droplet, Layers, Palette, ShieldCheck, Sparkles, Hammer } from "lucide-react";
import { useT } from "@/i18n";

const ICONS = [Droplet, Layers, Palette, ShieldCheck];
const SPANS = ["md:col-span-5", "md:col-span-7", "md:col-span-7", "md:col-span-5"];

const ProductShowcase = () => {
  const { t } = useT();
  const features = t.product.features.map((f, i) => ({ ...f, icon: ICONS[i], span: SPANS[i], accent: i === 0 }));
  return (
    <section id="product" data-testid="product-section" className="relative py-24 sm:py-32 bg-[#faf7f2]">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-20 items-end">
          <div className="md:col-span-7">
            <div className="overline mb-6">{t.product.overline}</div>
            <h2 className="font-display text-4xl sm:text-6xl tracking-tight leading-[1.02]">
              {t.product.title}<br />{t.product.title2}
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-zinc-600 leading-relaxed text-lg">{t.product.sub}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-px bg-black/10 border border-black/10">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <div
                key={i}
                data-testid={`feature-card-${i}`}
                className={`relative ${f.span} bg-[#faf7f2] p-8 sm:p-10 group hover:bg-white transition-colors duration-500`}
              >
                <div className="flex items-start justify-between mb-12">
                  <div className={`w-12 h-12 flex items-center justify-center border ${f.accent ? "border-[#ea7a1f] bg-[#ea7a1f]/10" : "border-black/15"}`}>
                    <Icon className={`w-5 h-5 ${f.accent ? "text-[#ea7a1f]" : "text-[#14141a]"}`} strokeWidth={1.5} />
                  </div>
                  <span className="font-display text-zinc-300 text-sm">0{i + 1}</span>
                </div>
                <h3 className="font-display text-2xl sm:text-3xl mb-4 tracking-tight">{f.title}</h3>
                <p className="text-zinc-600 leading-relaxed max-w-md">{f.body}</p>
                <div className="absolute bottom-0 left-0 h-px w-0 bg-[#ea7a1f] group-hover:w-full transition-all duration-700" />
              </div>
            );
          })}
        </div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6 relative aspect-[4/5] overflow-hidden">
            <img
              src="/generated/macro_texture.png"
              alt="Close-up of exposed-aggregate concrete surface"
              className="w-full h-full object-cover hover:scale-105 transition-all duration-1000"
            />
          </div>
          <div className="md:col-span-6">
            <div className="overline mb-6">{t.product.callout.overline}</div>
            <h3 className="font-display text-3xl sm:text-5xl mb-8 tracking-tight leading-[1.05]">
              {t.product.callout.h}<br /><span className="text-[#ea7a1f]">{t.product.callout.hl}</span>
            </h3>
            <ul className="space-y-5 text-zinc-700">
              {t.product.callout.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-4 border-b border-black/5 pb-5">
                  <Sparkles className="w-4 h-4 text-[#ea7a1f] mt-1.5 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <a
              href="https://youtu.be/n-mqJPlUVZU"
              target="_blank"
              rel="noreferrer"
              data-testid="watch-video-link"
              className="inline-flex items-center gap-3 mt-10 text-[#ea7a1f] hover:text-[#f08b34] copper-line"
            >
              <Hammer className="w-4 h-4" />
              {t.product.callout.video}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductShowcase;
